import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { ScreenBackdrop } from './ScreenBackdrop';
import { ChevronRight, CopyIcon, DiagonalArrow } from './icons';
import { IncptIcon } from '../icons/IncptIcon';
import { useTelegram } from '../../hooks/useTelegram';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';

/** Off-chain INCPT receive — share Telegram ID (no on-chain address until listing). */
export function IncptReceiveScreen({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();
  const { userId } = useTelegram();
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const id = userId ? String(userId) : '';

  const copy = () => {
    if (!id) return;
    navigator.clipboard?.writeText(id).then(
      () => {
        setCopied(true);
        setToast(t('coin.copied'));
        window.setTimeout(() => {
          setCopied(false);
          setToast(null);
        }, 1500);
      },
      () => {},
    );
  };

  const share = () => {
    if (!id) return;
    const wa = window.Telegram?.WebApp;
    if (wa?.openTelegramLink) {
      wa.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(id)}`);
    } else if (navigator.share) {
      void navigator.share({ text: id }).catch(() => {});
    }
  };

  return (
    <div className="relative flex h-full min-h-full flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex h-full min-h-full flex-col px-4 pb-[max(1.25rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <div className="relative flex h-10 items-center justify-center">
          {!tgBack && (
            <button
              onClick={onClose}
              className="absolute left-0 flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.35] text-white"
              aria-label="Back"
            >
              <ChevronRight size={18} className="rotate-180 text-white" />
            </button>
          )}
          <div className="flex items-center gap-1.5 rounded-full bg-white py-1.5 pl-1.5 pr-3 shadow-card">
            <IncptIcon size={24} />
            <span className="text-[14px] font-medium text-ink">{t('incpt.receive.title')}</span>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <div className="rounded-[28px] bg-white p-6 shadow-card">
            <QRCodeSVG
              value={id || ' '}
              size={208}
              bgColor="#ffffff"
              fgColor="#121212"
              level="M"
              marginSize={0}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <span className="text-[13px] text-muted">{t('receive.telegramId')}</span>
          <span className="text-[12px] text-muted">{t('receive.telegramIdHint')}</span>
        </div>
        <button
          onClick={copy}
          disabled={!id}
          className="mt-2 flex items-center gap-3 rounded-[20px] bg-white p-4 text-left shadow-card disabled:opacity-50"
        >
          <span className="min-w-0 flex-1 text-[15px] text-ink">{id || t('common.loading')}</span>
          <CopyIcon size={22} className={copied ? 'text-positive' : 'text-[#377dff]'} />
        </button>

        <p className="mt-5 px-4 text-center text-[13px] leading-[1.5] text-muted">
          {t('incpt.receive.hint')}
        </p>
        <p className="mt-2 px-4 text-center text-[13px] leading-[1.5] text-muted">
          {t('incpt.receive.warn')}
        </p>

        <div className="flex-1" />

        <button
          onClick={share}
          disabled={!id}
          className="flex h-14 items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90 disabled:opacity-50"
        >
          {t('receive.share')}
          <DiagonalArrow size={20} className="text-white" />
        </button>
      </div>

      {toast && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1rem)]">
          <div className="animate-toast flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-[14px] font-medium text-white shadow-[0px_10px_30px_rgba(0,0,0,0.25)]">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
